import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, LoginRequest, RegisterRequest, UpdateProfileRequest } from '@/types'
import { authApi, usersApi } from '@/api/client'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(localStorage.getItem('access_token'))
  const refreshToken = ref<string | null>(localStorage.getItem('refresh_token'))
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const userInitials = computed(() => {
    if (!user.value) return ''
    const name = user.value.full_name || user.value.username
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  })

  // Decode JWT to get user info
  const decodeToken = (token: string): any => {
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      console.error('Error decoding token:', error)
      return null
    }
  }

  // Check if token is expired
  const isTokenExpired = (token: string): boolean => {
    const payload = decodeToken(token)
    if (!payload || !payload.exp) return true
    return Date.now() >= payload.exp * 1000
  }

  // Actions
  const login = async (credentials: LoginRequest) => {
    loading.value = true
    error.value = null
    try {
      const response = await authApi.login(credentials)

      // Store tokens
      accessToken.value = response.access_token
      refreshToken.value = response.refresh_token
      localStorage.setItem('access_token', response.access_token)
      localStorage.setItem('refresh_token', response.refresh_token)

      // Store user data
      user.value = response.user

      console.log('✅ Login successful via Bheem Passport')
      return response
    } catch (err: any) {
      error.value = err.response?.data?.detail || err.message || 'Login failed'
      console.error('❌ Login error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const register = async (data: RegisterRequest) => {
    loading.value = true
    error.value = null
    try {
      const response = await authApi.register(data)

      // Store tokens if returned
      if (response.access_token) {
        accessToken.value = response.access_token
        refreshToken.value = response.refresh_token
        localStorage.setItem('access_token', response.access_token)
        localStorage.setItem('refresh_token', response.refresh_token)
      }

      // Store user data
      if (response.user) {
        user.value = response.user
      }

      console.log('✅ Registration successful via Bheem Passport')
      return response
    } catch (err: any) {
      error.value = err.response?.data?.detail || err.message || 'Registration failed'
      console.error('❌ Registration error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const refreshAccessToken = async (): Promise<boolean> => {
    if (!refreshToken.value) {
      console.error('No refresh token available')
      return false
    }

    try {
      const response = await authApi.refreshToken(refreshToken.value)

      accessToken.value = response.access_token
      refreshToken.value = response.refresh_token
      localStorage.setItem('access_token', response.access_token)
      localStorage.setItem('refresh_token', response.refresh_token)

      if (response.user) {
        user.value = response.user
      }

      console.log('✅ Token refreshed successfully')
      return true
    } catch (err) {
      console.error('❌ Token refresh failed:', err)
      // Logout on refresh failure
      await logout()
      return false
    }
  }

  const fetchUser = async () => {
    if (!accessToken.value) return

    // Check if token is expired
    if (isTokenExpired(accessToken.value)) {
      console.log('Access token expired, attempting refresh...')
      const refreshed = await refreshAccessToken()
      if (!refreshed) {
        throw new Error('Session expired. Please login again.')
      }
    }

    loading.value = true
    error.value = null
    try {
      const response = await authApi.getCurrentUser()
      user.value = response
      return response
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch user'
      // Try to refresh token on 401
      if (err.response?.status === 401) {
        const refreshed = await refreshAccessToken()
        if (refreshed) {
          // Retry fetching user
          const response = await authApi.getCurrentUser()
          user.value = response
          return response
        } else {
          logout()
        }
      }
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateProfile = async (data: UpdateProfileRequest) => {
    loading.value = true
    error.value = null
    try {
      const response = await usersApi.updateProfile(data)
      user.value = response.data
      return response
    } catch (err: any) {
      error.value = err.message || 'Failed to update profile'
      throw err
    } finally {
      loading.value = false
    }
  }

  const uploadAvatar = async (file: File) => {
    loading.value = true
    error.value = null
    try {
      const response = await usersApi.uploadAvatar(file)
      if (user.value) {
        user.value.avatar_url = response.data.avatar_url
      }
      return response
    } catch (err: any) {
      error.value = err.message || 'Failed to upload avatar'
      throw err
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      if (accessToken.value) {
        await authApi.logout()
      }
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      user.value = null
      accessToken.value = null
      refreshToken.value = null
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      console.log('✅ Logged out successfully')
    }
  }

  // Initialize user from token on app load
  const initializeAuth = async () => {
    if (accessToken.value) {
      try {
        await fetchUser()
      } catch (error) {
        console.error('Failed to initialize auth:', error)
      }
    }
  }

  return {
    // State
    user,
    accessToken,
    refreshToken,
    loading,
    error,
    // Getters
    isAuthenticated,
    userInitials,
    // Actions
    login,
    register,
    fetchUser,
    updateProfile,
    uploadAvatar,
    logout,
    refreshAccessToken,
    initializeAuth,
    isTokenExpired,
  }
})
